// Centralized vertex type metadata — single source of truth for UI display
// properties, categories, icons, styles, and vertex creation.

import type { Vertex } from '@shared/hydraulic'
import {
  createJunction,
  createPressureBoundary,
  createFlowDemand,
  createCentrifugalPump,
  createControlValve,
  createTank,
  createBalancingOrifice,
  createFixedLoss,
  createStrainer,
  createCheckValve,
  createReducer,
  createCurveLoss,
  createBlower,
  createFan,
  createCompressor,
  createPressureGain,
  createPressureReliefValve,
  createThreeWayValve,
  createNozzleMeter,
  createOrificeMeter,
  createVenturiMeter,
  createHeatSourceSink,
  createHeatExchanger,
} from '@shared/hydraulic'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface NodeStyle {
  bgVar: string
  border: string
  color: string
  shape: string
  size: string
}

export interface VertexTypeMeta {
  label: string
  abbreviation: string
  icon: string
  nodeStyle: NodeStyle
  minimapVar: string
  treeCategory: string
  treeDotColor: string
  addable: boolean
  addMenuCategory?: string
}

// ─── Default fallbacks ───────────────────────────────────────────────────────

const DEFAULT_NODE_STYLE: NodeStyle = {
  bgVar: 'var(--import-node-bg-junction)',
  border: 'border-[var(--import-text-secondary)]',
  color: 'text-[var(--import-text-secondary)]',
  shape: 'rounded-full',
  size: 'w-5 h-5',
}

const DEFAULT_ICON = '\u00B7'
const DEFAULT_MINIMAP_VAR = '--import-text-secondary'

// ─── Registry ────────────────────────────────────────────────────────────────

export const VERTEX_TYPE_REGISTRY: Record<string, VertexTypeMeta> = {
  // ── Pressure Sources ─────────────────────────────────────────────────────
  pressure_boundary: {
    label: 'Pressure Boundary',
    abbreviation: 'PB',
    icon: '\u25A4',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-reservoir)',
      border: 'border-cyan-400',
      color: 'text-cyan-400',
      shape: 'rounded-lg',
      size: 'w-5 h-5',
    },
    minimapVar: '--import-node-reservoir',
    treeCategory: 'Boundary Devices',
    treeDotColor: 'bg-cyan-400',
    addable: true,
    addMenuCategory: 'Boundary Devices',
  },
  tank: {
    label: 'Tank',
    abbreviation: 'Tank',
    icon: '\u25A4',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-reservoir)',
      border: 'border-cyan-400',
      color: 'text-cyan-400',
      shape: 'rounded-lg',
      size: 'w-5 h-5',
    },
    minimapVar: '--import-node-reservoir',
    treeCategory: 'Boundary Devices',
    treeDotColor: 'bg-cyan-400',
    addable: true,
    addMenuCategory: 'Boundary Devices',
  },

  // ── Pumps & Compressors ──────────────────────────────────────────────────
  centrifugal_pump: {
    label: 'Centrifugal Pump',
    abbreviation: 'Pump',
    icon: '\u27F3',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-pump)',
      border: 'border-yellow-400',
      color: 'text-yellow-400',
      shape: 'rounded-full',
      size: 'w-5 h-5',
    },
    minimapVar: '--import-node-pump',
    treeCategory: 'Energy Adding Devices',
    treeDotColor: 'bg-yellow-400',
    addable: true,
    addMenuCategory: 'Energy Adding Devices',
  },
  compressor: {
    label: 'Compressor',
    abbreviation: 'Compressor',
    icon: '//',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-pump)',
      border: 'border-yellow-400',
      color: 'text-yellow-400',
      shape: 'rounded-full',
      size: 'w-5 h-5',
    },
    minimapVar: '--import-node-pump',
    treeCategory: 'Energy Adding Devices',
    treeDotColor: 'bg-yellow-400',
    addable: true,
    addMenuCategory: 'Energy Adding Devices',
  },
  fan: {
    label: 'Fan',
    abbreviation: 'Fan',
    icon: '\u2716',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-pump)',
      border: 'border-yellow-400',
      color: 'text-yellow-400',
      shape: 'rounded-full',
      size: 'w-5 h-5',
    },
    minimapVar: '--import-node-pump',
    treeCategory: 'Energy Adding Devices',
    treeDotColor: 'bg-yellow-400',
    addable: true,
    addMenuCategory: 'Energy Adding Devices',
  },
  blower: {
    label: 'Blower',
    abbreviation: 'Blower',
    icon: '\u2733',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-pump)',
      border: 'border-yellow-400',
      color: 'text-yellow-400',
      shape: 'rounded-full',
      size: 'w-5 h-5',
    },
    minimapVar: '--import-node-pump',
    treeCategory: 'Energy Adding Devices',
    treeDotColor: 'bg-yellow-400',
    addable: true,
    addMenuCategory: 'Energy Adding Devices',
  },

  // ── Valves ───────────────────────────────────────────────────────────────
  control_valve: {
    label: 'Control Valve',
    abbreviation: 'CV',
    icon: '\u25C6',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-valve)',
      border: 'border-violet-400',
      color: 'text-violet-400',
      shape: 'rounded rotate-45',
      size: 'w-5 h-5',
    },
    minimapVar: '--import-node-valve',
    treeCategory: 'Valves',
    treeDotColor: 'bg-violet-400',
    addable: true,
    addMenuCategory: 'Valves',
  },
  three_way_valve: {
    label: 'Three-Way Valve',
    abbreviation: '3WV',
    icon: 'Y',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-valve)',
      border: 'border-violet-400',
      color: 'text-violet-400',
      shape: 'rounded-lg',
      size: 'w-5 h-5',
    },
    minimapVar: '--import-node-valve',
    treeCategory: 'Valves',
    treeDotColor: 'bg-violet-400',
    addable: true,
    addMenuCategory: 'Valves',
  },
  pressure_relief_valve: {
    label: 'Pressure Relief Valve',
    abbreviation: 'PRV',
    icon: '\u2191',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-valve)',
      border: 'border-violet-400',
      color: 'text-violet-400',
      shape: 'rounded-lg',
      size: 'w-5 h-5',
    },
    minimapVar: '--import-node-valve',
    treeCategory: 'Valves',
    treeDotColor: 'bg-violet-400',
    addable: true,
    addMenuCategory: 'Valves',
  },

  // ── Passive ──────────────────────────────────────────────────────────────
  junction: {
    label: 'Junction',
    abbreviation: 'Junction',
    icon: '\u00B7',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-junction)',
      border: 'border-[var(--import-text-secondary)]',
      color: 'text-[var(--import-text-secondary)]',
      shape: 'rounded-full',
      size: 'w-5 h-5',
    },
    minimapVar: '--import-text-secondary',
    treeCategory: 'Passive',
    treeDotColor: 'bg-emerald-300',
    addable: true,
    addMenuCategory: 'Passive',
  },
  curve_loss: {
    label: 'Curve Loss',
    abbreviation: 'CL',
    icon: '~',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-junction)',
      border: 'border-emerald-400',
      color: 'text-emerald-400',
      shape: 'rounded',
      size: 'w-5 h-5',
    },
    minimapVar: DEFAULT_MINIMAP_VAR,
    treeCategory: 'Passive',
    treeDotColor: 'bg-emerald-300',
    addable: true,
    addMenuCategory: 'Passive',
  },
  fixed_loss: {
    label: 'Fixed Loss',
    abbreviation: 'FL',
    icon: 'K',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-junction)',
      border: 'border-emerald-400',
      color: 'text-emerald-400',
      shape: 'rounded',
      size: 'w-5 h-5',
    },
    minimapVar: DEFAULT_MINIMAP_VAR,
    treeCategory: 'Passive',
    treeDotColor: 'bg-emerald-300',
    addable: true,
    addMenuCategory: 'Passive',
  },
  strainer: {
    label: 'Strainer',
    abbreviation: 'ST',
    icon: '≡',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-junction)',
      border: 'border-emerald-400',
      color: 'text-emerald-400',
      shape: 'rounded',
      size: 'w-5 h-5',
    },
    minimapVar: DEFAULT_MINIMAP_VAR,
    treeCategory: 'Passive',
    treeDotColor: 'bg-emerald-300',
    addable: true,
    addMenuCategory: 'Passive',
  },
  check_valve: {
    label: 'Check Valve',
    abbreviation: 'CK',
    icon: '▷|',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-junction)',
      border: 'border-emerald-400',
      color: 'text-emerald-400',
      shape: 'rounded',
      size: 'w-5 h-5',
    },
    minimapVar: DEFAULT_MINIMAP_VAR,
    treeCategory: 'Passive',
    treeDotColor: 'bg-emerald-300',
    addable: true,
    addMenuCategory: 'Passive',
  },
  reducer: {
    label: 'Reducer',
    abbreviation: 'Reducer',
    icon: '\u25B7',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-junction)',
      border: 'border-emerald-400',
      color: 'text-emerald-400',
      shape: 'rounded',
      size: 'w-5 h-5',
    },
    minimapVar: DEFAULT_MINIMAP_VAR,
    treeCategory: 'Passive',
    treeDotColor: 'bg-emerald-300',
    addable: true,
    addMenuCategory: 'Passive',
  },
  heat_exchanger: {
    label: 'Heat Exchanger',
    abbreviation: 'HX',
    icon: '\u224B',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-hx)',
      border: 'border-red-400',
      color: 'text-red-400',
      shape: 'rounded',
      size: 'w-5 h-5',
    },
    minimapVar: '--import-node-hx',
    treeCategory: 'Passive',
    treeDotColor: 'bg-emerald-300',
    addable: true,
    addMenuCategory: 'Passive',
  },
  heat_source_sink: {
    label: 'Heat Source/Sink',
    abbreviation: 'HSS',
    icon: '\u224B',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-hx)',
      border: 'border-red-400',
      color: 'text-red-400',
      shape: 'rounded',
      size: 'w-5 h-5',
    },
    minimapVar: '--import-node-hx',
    treeCategory: 'Passive',
    treeDotColor: 'bg-emerald-300',
    addable: true,
    addMenuCategory: 'Passive',
  },
  balancing_orifice: {
    label: 'Balancing Orifice',
    abbreviation: 'BO',
    icon: '\u2295',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-junction)',
      border: 'border-emerald-400',
      color: 'text-emerald-400',
      shape: 'rounded-full',
      size: 'w-5 h-5',
    },
    minimapVar: DEFAULT_MINIMAP_VAR,
    treeCategory: 'Passive',
    treeDotColor: 'bg-emerald-300',
    addable: true,
    addMenuCategory: 'Passive',
  },
  pressure_gain: {
    label: 'Pressure Gain',
    abbreviation: 'PG',
    icon: '\u25B3',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-pump)',
      border: 'border-yellow-400',
      color: 'text-yellow-400',
      shape: 'rounded-full',
      size: 'w-5 h-5',
    },
    minimapVar: '--import-node-pump',
    treeCategory: 'Energy Adding Devices',
    treeDotColor: 'bg-emerald-300',
    addable: true,
    addMenuCategory: 'Energy Adding Devices',
  },

  // ── Flow Demands ─────────────────────────────────────────────────────────
  flow_demand: {
    label: 'Flow Demand',
    abbreviation: 'FD',
    icon: '\u2192',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-flow)',
      border: 'border-orange-400',
      color: 'text-orange-400',
      shape: 'rounded-full',
      size: 'w-5 h-5',
    },
    minimapVar: '--import-node-flow',
    treeCategory: 'Boundary Devices',
    treeDotColor: 'bg-orange-400',
    addable: true,
    addMenuCategory: 'Boundary Devices',
  },

  // ── Meters ───────────────────────────────────────────────────────────────
  orifice_meter: {
    label: 'Orifice Meter',
    abbreviation: 'OM',
    icon: '\u2296',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-junction)',
      border: 'border-sky-400',
      color: 'text-sky-400',
      shape: 'rounded-full',
      size: 'w-5 h-5',
    },
    minimapVar: DEFAULT_MINIMAP_VAR,
    treeCategory: 'Meters',
    treeDotColor: 'bg-sky-400',
    addable: true,
    addMenuCategory: 'Meters',
  },
  venturi_meter: {
    label: 'Venturi Meter',
    abbreviation: 'VM',
    icon: '\u22C8',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-junction)',
      border: 'border-sky-400',
      color: 'text-sky-400',
      shape: 'rounded-full',
      size: 'w-5 h-5',
    },
    minimapVar: DEFAULT_MINIMAP_VAR,
    treeCategory: 'Meters',
    treeDotColor: 'bg-sky-400',
    addable: true,
    addMenuCategory: 'Meters',
  },
  nozzle_meter: {
    label: 'Nozzle Meter',
    abbreviation: 'NM',
    icon: '\u25B7',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-junction)',
      border: 'border-sky-400',
      color: 'text-sky-400',
      shape: 'rounded-full',
      size: 'w-5 h-5',
    },
    minimapVar: DEFAULT_MINIMAP_VAR,
    treeCategory: 'Meters',
    treeDotColor: 'bg-sky-400',
    addable: true,
    addMenuCategory: 'Meters',
  },

  // ── Retired ──────────────────────────────────────────────────────────────
  // PIPE-46: `tee` and `bend` are RETIRED — `addable: false` drops them from
  // the add menu (and with them the Fittings category, whose only members they
  // were), while the DISPLAY metadata below stays so vertices already saved in
  // customer models keep their label, icon and Passive tree grouping. Display
  // metadata is not creation permission; deleting the entry outright would
  // demote existing bends to an unlabelled "Other" for no gain.
  //
  // ⚠️ `addable: false` only hides the menu entry. What actually makes them
  // uncreatable is the absence of a factory in BOTH dispatchers — FACTORY_MAP
  // below, and `defaultVertexForType` in shared/hydraulic/factories. All three
  // must stay in step or a door reopens.
  tee: {
    label: 'Tee',
    abbreviation: 'Tee',
    icon: '\u22A4',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-junction)',
      border: 'border-emerald-300',
      color: 'text-emerald-300',
      shape: 'rounded-full',
      size: 'w-5 h-5',
    },
    minimapVar: DEFAULT_MINIMAP_VAR,
    treeCategory: 'Passive',
    treeDotColor: 'bg-emerald-300',
    addable: false,
  },
  bend: {
    label: 'Bend',
    abbreviation: 'Bend',
    icon: '\u00B7',
    nodeStyle: {
      bgVar: 'var(--import-node-bg-junction)',
      border: 'border-[var(--import-text-secondary)]',
      color: 'text-[var(--import-text-secondary)]',
      shape: 'rounded-full',
      size: 'w-5 h-5',
    },
    minimapVar: DEFAULT_MINIMAP_VAR,
    treeCategory: 'Passive',
    treeDotColor: 'bg-emerald-300',
    addable: false,
  },
}

// ─── Derived: Labels ─────────────────────────────────────────────────────────

export const VERTEX_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(VERTEX_TYPE_REGISTRY).map(([type, meta]): [string, string] => [type, meta.label]),
)

// ─── Derived: Addable type categories (for toolbar & context menus) ──────────

const ADD_MENU_ORDER = ['Boundary Devices', 'Energy Adding Devices', 'Valves', 'Passive', 'Meters', 'Fittings']

export const VERTEX_TYPE_CATEGORIES: Array<{ heading: string; types: string[] }> =
  ADD_MENU_ORDER.map((heading): { heading: string; types: Array<string>; } => ({
    heading,
    types: Object.entries(VERTEX_TYPE_REGISTRY)
      .filter(([, meta]): boolean => meta.addable && meta.addMenuCategory === heading)
      // Order devices alphabetically by display label within each group (B8).
      .sort(([, a], [, b]): number => a.label.localeCompare(b.label))
      .map(([type]): string => type),
  })).filter((cat): boolean => cat.types.length > 0)

/**
 * Flattened `VERTEX_TYPE_CATEGORIES` — every type a user is allowed to place.
 *
 * Membership MUST be tested against this Set, never `type in
 * VERTEX_TYPE_LABELS`: that object has a normal prototype, so `'constructor'`,
 * `'toString'` and friends pass an `in` check and then resolve to a FUNCTION,
 * which blows up on `.localeCompare`. Persisted, user-editable storage (the
 * palette's favorites list) feeds straight into this check, so a value like
 * `["tank","constructor"]` would otherwise crash the panel on every load until
 * site data was cleared.
 *
 * Lives here rather than in DrawingPanel so `use-palette-favorites` can prune
 * against it without importing the component that consumes the hook.
 */
export const ADDABLE_TYPES: ReadonlySet<string> = new Set(
  VERTEX_TYPE_CATEGORIES.flatMap((c): string[] => c.types),
)

// ─── Derived: ObjectTree categories ──────────────────────────────────────────

interface TreeCategoryConfig {
  label: string
  dotColor: string
}

const TREE_ORDER = ['Boundary Devices', 'Energy Adding Devices', 'Valves', 'Passive', 'Meters']

export const TREE_VERTEX_CATEGORIES: Array<{ label: string; dotColor: string; types: string[] }> =
  TREE_ORDER.map((label): { label: string; dotColor: string; types: Array<string>; } => {
    const types = Object.entries(VERTEX_TYPE_REGISTRY)
      .filter(([, meta]): boolean => meta.treeCategory === label)
      .map(([type]): string => type)
    const first = types[0]
    const dotColor = first
      ? VERTEX_TYPE_REGISTRY[first]?.treeDotColor ?? 'bg-[var(--import-text-secondary)]'
      : 'bg-[var(--import-text-secondary)]'
    return { label, dotColor, types }
  }).filter((cat): boolean => cat.types.length > 0)

export const TREE_CATEGORY_OTHER: TreeCategoryConfig = {
  label: 'Other',
  dotColor: 'bg-[var(--import-text-secondary)]',
}

export function getCategoryForType(type: string): TreeCategoryConfig {
  for (const cat of TREE_VERTEX_CATEGORIES) {
    if (cat.types.includes(type)) return cat
  }
  return TREE_CATEGORY_OTHER
}

// ─── Derived: Node styles & icons (for DeviceNode) ───────────────────────────

export const KNOWN_TYPES = new Set(Object.keys(VERTEX_TYPE_REGISTRY))

export function getNodeStyle(type: string): NodeStyle {
  return VERTEX_TYPE_REGISTRY[type]?.nodeStyle ?? DEFAULT_NODE_STYLE
}

export function getNodeIcon(type: string): string {
  return VERTEX_TYPE_REGISTRY[type]?.icon ?? DEFAULT_ICON
}

/**
 * Return the rendered pixel dimensions of a node type's body.
 *
 * Parses the Tailwind `size` class in the node style. Tailwind's numeric
 * scale is 4 px per unit, so `w-5` = 20 px. Arbitrary-value classes like
 * `w-[22px]` are parsed out of the brackets, and width/height are read
 * independently — though every registered type is `w-5 h-5` today, so
 * neither of those branches is currently reached by a real type.
 *
 * Falls back to DEFAULT_NODE_STYLE (`w-5 h-5` → 20×20) for unknown types
 * or unparseable classes — the same size every known type uses, so an
 * unrecognised device renders like a known one.
 *
 * These dimensions are the SOURCE OF TRUTH for anything that needs to
 * know the visual extent of a node — placement, alignment tools,
 * bounding-box computation, and the box a pipe terminal exits through.
 * They no longer affect grid snapping: `diagram.position` is the device's
 * anchor, so snapping it needs no dimensions.
 *
 * Keeps the registry as the single source of truth; callers don't have
 * to re-derive sizes from Tailwind strings themselves.
 */
export function getNodeDimensions(type: string): { width: number; height: number } {
  const sizeClass = getNodeStyle(type).size
  return { width: parseSizeDimension(sizeClass, 'w'), height: parseSizeDimension(sizeClass, 'h') }
}

// Semgrep flags `new RegExp(...)` with a template arg even when the input
// is a strict union. Swap to literal regexes picked by a type-narrowed
// switch so the patterns are statically visible.
const ARBITRARY_SIZE_RE = {
  w: /w-\[(\d+)px\]/,
  h: /h-\[(\d+)px\]/,
} as const
const SCALE_SIZE_RE = {
  w: /w-(\d+)(?!\S)/,
  h: /h-(\d+)(?!\S)/,
} as const

function parseSizeDimension(sizeClass: string, prefix: 'w' | 'h'): number {
  // Arbitrary value: "w-[22px]" or "h-[22px]"
  const arbitraryMatch = sizeClass.match(ARBITRARY_SIZE_RE[prefix])
  if (arbitraryMatch?.[1]) return parseInt(arbitraryMatch[1], 10)

  // Tailwind scale: "w-10" → 10 × 4 = 40 px
  const scaleMatch = sizeClass.match(SCALE_SIZE_RE[prefix])
  if (scaleMatch?.[1]) return parseInt(scaleMatch[1], 10) * 4

  return 32 // sensible default matching most mid-sized nodes
}

// ─── Derived: MiniMap color (for SchematicCanvas) ────────────────────────────

export function getMinimapVar(type: string): string {
  return VERTEX_TYPE_REGISTRY[type]?.minimapVar ?? DEFAULT_MINIMAP_VAR
}

// ─── Vertex creation ─────────────────────────────────────────────────────────

// Factory dispatch table — maps type string to factory call with UI-appropriate
// defaults. Overrides factory defaults where the UI convention differs
// (e.g. short names, atmospheric pressure for PB, kg/s for flow demand).
const FACTORY_MAP: Record<string, (id: string, pos: { x: number; y: number }) => Vertex> = {
  junction: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "junction"; elevation: { value: number; unit: string; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; } =>
    createJunction(id, { diagram: { position: pos } }),
  pressure_boundary: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "pressure_boundary"; elevation: { value: number; unit: string; }; pressure: { value: number; unit: string; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; pressure_type?: "total" | "static" | undefined; } =>
    createPressureBoundary(id, { name: `PB ${id}`, pressure: { value: 101325, unit: 'Pa' }, diagram: { position: pos } }),
  flow_demand: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "flow_demand"; elevation: { value: number; unit: string; }; operation: { mode: "flow_in"; value: number; unit: string; } | { mode: "flow_out"; value: number; unit: string; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; } =>
    createFlowDemand(id, { name: `FD ${id}`, operation: { mode: 'flow_out', value: 0, unit: 'kg/s' }, diagram: { position: pos } }),
  centrifugal_pump: (id, pos) =>
    createCentrifugalPump(id, { name: `Pump ${id}`, diagram: { position: pos } }),
  control_valve: (id, pos) =>
    createControlValve(id, { name: `CV ${id}`, diagram: { position: pos } }),
  tank: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "tank"; elevation: { value: number; unit: string; }; surface_pressure: { value: number; unit: string; }; liquid_level: { value: number; unit: string; }; penetrations: Array<{ edge_id: string; edge_name: string; height: { value: number; unit: string; }; }>; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; fluid_zone_id?: string | undefined; tank_geometry?: { shape: "spherical"; orientation: "no_orientation" | "horizontal" | "vertical"; diameter: { value: number; unit: string; }; } | { shape: "rectangular"; orientation: "no_orientation" | "horizontal" | "vertical"; length: { value: number; unit: string; }; width: { value: number; unit: string; }; height: { value: number; unit: string; }; } | { shape: "cylindrical_horizontal"; orientation: "no_orientation" | "horizontal" | "vertical"; body: { diameter: { value: number; unit: string; }; length: { value: number; unit: string; }; }; left_cap: { cap_shape: "spherical" | "flat" | "conical" | "ellipsoid"; placement: "left_or_right" | "bottom" | "top"; diameter?: { value: number; unit: string; } | undefined; length?: { value: number; unit: string; } | undefined; }; right_cap: { cap_shape: "spherical" | "flat" | "conical" | "ellipsoid"; placement: "left_or_right" | "bottom" | "top"; diameter?: { value: number; unit: string; } | undefined; length?: { value: number; unit: string; } | undefined; }; } | { shape: "cylindrical_vertical"; orientation: "no_orientation" | "horizontal" | "vertical"; body: { diameter: { value: number; unit: string; }; length: { value: number; unit: string; }; }; bottom_cap: { cap_shape: "spherical" | "flat" | "conical" | "ellipsoid"; placement: "left_or_right" | "bottom" | "top"; diameter?: { value: number; unit: string; } | undefined; length?: { value: number; unit: string; } | undefined; }; top_cap: { cap_shape: "spherical" | "flat" | "conical" | "ellipsoid"; placement: "left_or_right" | "bottom" | "top"; diameter?: { value: number; unit: string; } | undefined; length?: { value: number; unit: string; } | undefined; }; } | { shape: "custom"; orientation: "no_orientation" | "horizontal" | "vertical"; curve: Array<{ level: { value: number; unit: string; }; volume: { value: number; unit: string; }; }>; } | undefined; } =>
    createTank(id, { diagram: { position: pos } }),
  balancing_orifice: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "balancing_orifice"; elevation: { value: number; unit: string; }; meter_diameter: { value: number; unit: string; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; device_material?: "alloy_steel" | "aluminum" | "brass" | "bronze" | "carbon_steel" | "crni_25_20" | "cuni_70_30" | "monel_66_29" | "monel_67_30" | "series_300_ss" | "series_400_ss" | "wrought_iron" | undefined; pipe_material?: "alloy_steel" | "aluminum" | "brass" | "bronze" | "carbon_steel" | "crni_25_20" | "cuni_70_30" | "monel_66_29" | "monel_67_30" | "series_300_ss" | "series_400_ss" | "wrought_iron" | undefined; } =>
    createBalancingOrifice(id, { diagram: { position: pos } }),
  fixed_loss: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "fixed_loss"; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; inlet_elevation?: { value: number; unit: string; } | undefined; outlet_elevation?: { value: number; unit: string; } | undefined; loss?: { value: number; unit: string; } | undefined; } =>
    createFixedLoss(id, { diagram: { position: pos } }),
  strainer: (id, pos) =>
    createStrainer(id, { name: `Strainer ${id}`, diagram: { position: pos } }),
  check_valve: (id, pos) =>
    createCheckValve(id, { name: `Check Valve ${id}`, diagram: { position: pos } }),
  reducer: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "reducer"; elevation: { value: number; unit: string; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; transition_length?: { value: number; unit: string; } | undefined; } =>
    createReducer(id, { diagram: { position: pos } }),
  curve_loss: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "curve_loss"; inlet_elevation: { value: number; unit: string; }; outlet_elevation: { value: number; unit: string; }; pressure_drop_curve: { points: Array<{ flow: { value: number; unit: string; }; pressure_drop: { value: number; unit: string; }; }>; description?: string | undefined; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; } =>
    createCurveLoss(id, { diagram: { position: pos } }),
  blower: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "blower"; elevation: { value: number; unit: string; }; operation: { mode: "pressure_differential"; value: number; unit: string; pressure_type?: "total" | "static" | undefined; } | { mode: "pressure_suction"; value: number; unit: string; pressure_type?: "total" | "static" | undefined; } | { mode: "pressure_discharge"; value: number; unit: string; pressure_type?: "total" | "static" | undefined; } | { mode: "flow_volumetric"; value: number; unit: string; } | { mode: "flow_mass"; value: number; unit: string; } | { mode: "flow_temperature_control"; value: number; unit: string; linked_device_id?: string | undefined; } | { mode: "specified_speed"; value: number; unit: string; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; blower_curve?: { curve_name: string; tables: Array<{ label: string; pressure_drop_line: { pressure_type: "total_pressure" | "static_pressure"; is_differential: boolean; total_pressure?: { value: number; unit: string; } | undefined; static_pressure?: { value: number; unit: string; } | undefined; total_pressure_dP?: { value: number; unit: string; } | undefined; static_pressure_dP?: { value: number; unit: string; } | undefined; }; pressure_drop_table: Array<{ speed: { value: number; unit: string; }; flow: { value: number; unit: string; }; power?: { value: number; unit: string; } | undefined; delta_temperature?: { value: number; unit: string; } | undefined; }>; }>; manufacturer?: string | undefined; model?: string | undefined; max_flow?: { value: number; unit: string; } | undefined; max_pressure_drop?: { value: number; unit: string; } | undefined; max_speed?: { value: number; unit: string; } | undefined; max_vacuum?: { value: number; unit: string; } | undefined; reference_density?: { value: number; unit: string; } | undefined; } | undefined; } =>
    createBlower(id, { diagram: { position: pos } }),
  fan: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "fan"; elevation: { value: number; unit: string; }; operation: { mode: "pressure_differential"; value: number; unit: string; pressure_type?: "static" | "total" | undefined; } | { mode: "pressure_suction"; value: number; unit: string; pressure_type?: "static" | "total" | undefined; } | { mode: "pressure_discharge"; value: number; unit: string; pressure_type?: "static" | "total" | undefined; } | { mode: "flow_volumetric"; value: number; unit: string; } | { mode: "flow_mass"; value: number; unit: string; } | { mode: "flow_temperature_control"; value: number; unit: string; linked_device_id?: string | undefined; } | { mode: "specified_speed"; value: number; unit: string; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; fan_curve?: { curve_name: string; table: Array<{ dP: { value: number; unit: string; }; flow: { value: number; unit: string; }; power?: { value: number; unit: string; } | undefined; efficiency?: { value: number; unit: string; } | undefined; }>; table_type: "static" | "total"; input_curve_type: "power" | "efficiency"; manufacturer?: string | undefined; model?: string | undefined; test_speed?: { value: number; unit: string; } | undefined; reference_density?: { value: number; unit: string; } | undefined; speed_range?: { min?: { value: number; unit: string; } | undefined; max?: { value: number; unit: string; } | undefined; } | undefined; stall_range?: { min?: { value: number; unit: string; } | undefined; max?: { value: number; unit: string; } | undefined; } | undefined; } | undefined; } =>
    createFan(id, { diagram: { position: pos } }),
  compressor: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "compressor"; elevation: { value: number; unit: string; }; operation: { mode: "pressure_differential"; value: number; unit: string; pressure_type?: "total" | "static" | undefined; } | { mode: "pressure_suction"; value: number; unit: string; pressure_type?: "total" | "static" | undefined; } | { mode: "pressure_discharge"; value: number; unit: string; pressure_type?: "total" | "static" | undefined; } | { mode: "flow_volumetric"; value: number; unit: string; } | { mode: "flow_mass"; value: number; unit: string; } | { mode: "flow_temperature_control"; value: number; unit: string; linked_device_id?: string | undefined; } | { mode: "specified_speed"; value: number; unit: string; } | { mode: "pressure_ratio"; value: number; unit: string; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; compressor_curve?: { curve_name: string; table: Array<{ flow: { value: number; unit: string; }; pressure_ratio: { value: number; unit: string; }; efficiency: { value: number; unit: string; }; }>; manufacturer?: string | undefined; model?: string | undefined; test_speed?: { value: number; unit: string; } | undefined; flow_unit?: string | undefined; reference_molecular_mass?: { value: number; unit: string; } | undefined; reference_specific_heat_ratio?: { value: number; unit: string; } | undefined; reference_pressure?: { value: number; unit: string; } | undefined; reference_temperature?: { value: number; unit: string; } | undefined; } | undefined; polytropic_exponent?: { method: "calculate_n" | "use_specific_heat_ratio" | "entered_n"; value?: number | undefined; } | undefined; } =>
    createCompressor(id, { diagram: { position: pos } }),
  pressure_gain: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "pressure_gain"; inlet_elevation: { value: number; unit: string; }; outlet_elevation: { value: number; unit: string; }; pressure_drop_curve: { points: Array<{ flow: { value: number; unit: string; }; pressure_drop: { value: number; unit: string; }; }>; description?: string | undefined; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; efficiency_ratio?: number | undefined; } =>
    createPressureGain(id, { diagram: { position: pos } }),
  pressure_relief_valve: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "pressure_relief_valve"; elevation: { value: number; unit: string; }; set_pressure: { value: number; unit: string; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; discharge_coefficient?: number | undefined; discharge_area?: { value: number; unit: string; } | undefined; back_pressure_correction_factor?: number | undefined; } =>
    createPressureReliefValve(id, { diagram: { position: pos } }),
  three_way_valve: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "three_way_valve"; elevation: { value: number; unit: string; }; operation_port_a: { mode: "flow_volumetric"; value: number; unit: string; } | { mode: "flow_mass"; value: number; unit: string; } | { mode: "pressure_differential"; value: number; unit: string; } | { mode: "pressure_inlet"; value: number; unit: string; } | { mode: "pressure_outlet"; value: number; unit: string; } | { mode: "valve_position"; value: number; unit: string; } | { mode: "fixed_flow_coefficient"; value: number; unit: string; }; operation_port_b: { mode: "flow_volumetric"; value: number; unit: string; } | { mode: "flow_mass"; value: number; unit: string; } | { mode: "pressure_differential"; value: number; unit: string; } | { mode: "pressure_inlet"; value: number; unit: string; } | { mode: "pressure_outlet"; value: number; unit: string; } | { mode: "valve_position"; value: number; unit: string; } | { mode: "fixed_flow_coefficient"; value: number; unit: string; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; port_a_edge_id?: string | undefined; port_b_edge_id?: string | undefined; } =>
    createThreeWayValve(id, { diagram: { position: pos } }),
  nozzle_meter: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "nozzle_meter"; elevation: { value: number; unit: string; }; nozzle_type: "long_radius" | "isa1932"; meter_diameter: { value: number; unit: string; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; device_material?: "alloy_steel" | "aluminum" | "brass" | "bronze" | "carbon_steel" | "crni_25_20" | "cuni_70_30" | "monel_66_29" | "monel_67_30" | "series_300_ss" | "series_400_ss" | "wrought_iron" | undefined; pipe_material?: "alloy_steel" | "aluminum" | "brass" | "bronze" | "carbon_steel" | "crni_25_20" | "cuni_70_30" | "monel_66_29" | "monel_67_30" | "series_300_ss" | "series_400_ss" | "wrought_iron" | undefined; } =>
    createNozzleMeter(id, { diagram: { position: pos } }),
  orifice_meter: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "orifice_meter"; elevation: { value: number; unit: string; }; pressure_taps: "corner" | "dhd" | "flange"; meter_diameter: { value: number; unit: string; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; device_material?: "alloy_steel" | "aluminum" | "brass" | "bronze" | "carbon_steel" | "crni_25_20" | "cuni_70_30" | "monel_66_29" | "monel_67_30" | "series_300_ss" | "series_400_ss" | "wrought_iron" | undefined; pipe_material?: "alloy_steel" | "aluminum" | "brass" | "bronze" | "carbon_steel" | "crni_25_20" | "cuni_70_30" | "monel_66_29" | "monel_67_30" | "series_300_ss" | "series_400_ss" | "wrought_iron" | undefined; } =>
    createOrificeMeter(id, { diagram: { position: pos } }),
  venturi_meter: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "venturi_meter"; elevation: { value: number; unit: string; }; mfg_method: "as_cast" | "machined" | "rough_welded"; divergence_angle: "seven_degrees" | "fifteen_degrees"; meter_diameter: { value: number; unit: string; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; device_material?: "alloy_steel" | "aluminum" | "brass" | "bronze" | "carbon_steel" | "crni_25_20" | "cuni_70_30" | "monel_66_29" | "monel_67_30" | "series_300_ss" | "series_400_ss" | "wrought_iron" | undefined; pipe_material?: "alloy_steel" | "aluminum" | "brass" | "bronze" | "carbon_steel" | "crni_25_20" | "cuni_70_30" | "monel_66_29" | "monel_67_30" | "series_300_ss" | "series_400_ss" | "wrought_iron" | undefined; } =>
    createVenturiMeter(id, { diagram: { position: pos } }),
  heat_source_sink: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "heat_source_sink"; pressure_drop_curve: { points: Array<{ flow: { value: number; unit: string; }; pressure_drop: { value: number; unit: string; }; }>; description?: string | undefined; }; thermal_calculation: { mode: "calculate_heat_transfer_rate" | "calculate_flow_rate" | "calculate_inlet_temperature" | "calculate_outlet_temperature" | "none"; is_flow_rate_user_entered: boolean | null; heat_transfer_rate?: { value: number; unit: string; } | undefined; flow_rate?: { value: number; unit: string; } | undefined; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; inlet_elevation?: { value: number; unit: string; } | undefined; outlet_elevation?: { value: number; unit: string; } | undefined; linked_device_id?: string | undefined; temperature_tolerance?: { value: number; unit: string; } | undefined; } =>
    createHeatSourceSink(id, { diagram: { position: pos } }),
  heat_exchanger: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "heat_exchanger"; pressure_drop_curve: { points: Array<{ flow: { value: number; unit: string; }; pressure_drop: { value: number; unit: string; }; }>; description?: string | undefined; }; hx_configuration: { configuration_type: "no_hx_configuration" | "single_pass_counter_current_flow" | "single_pass_parallel_flow" | "shell_and_tube_1_shell" | "shell_and_tube_n_shell" | "cross_flow_hcr_max_mixed" | "cross_flow_hcr_min_mixed" | "specify_hx_configuration_factor" | "specify_hx_ntu"; num_shell_passes?: number | undefined; ntu?: number | undefined; configuration_factor?: number | undefined; }; thermal_calculation: { modeled_side: { mode: "calculate_heat_transfer_rate" | "calculate_flow_rate" | "calculate_inlet_temperature" | "calculate_outlet_temperature" | "none"; is_flow_rate_user_entered: boolean | null; heat_transfer_rate?: { value: number; unit: string; } | undefined; flow_rate?: { value: number; unit: string; } | undefined; }; unmodeled_side: { mode: "calculate_heat_transfer_rate" | "calculate_flow_rate" | "calculate_inlet_temperature" | "calculate_outlet_temperature" | "none"; is_flow_rate_user_entered: boolean | null; heat_transfer_rate?: { value: number; unit: string; } | undefined; flow_rate?: { value: number; unit: string; } | undefined; inlet_fluid_zone_id?: string | undefined; outlet_fluid_zone_id?: string | undefined; }; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; inlet_elevation?: { value: number; unit: string; } | undefined; outlet_elevation?: { value: number; unit: string; } | undefined; linked_device_id?: string | undefined; ua_available?: { heat_transfer_coefficient?: { value: number; unit: string; } | undefined; heat_transfer_area?: { value: number; unit: string; } | undefined; fouling_factor?: { value: number; unit: string; } | undefined; } | undefined; temperature_tolerance?: { value: number; unit: string; } | undefined; } =>
    createHeatExchanger(id, { diagram: { position: pos } }),
  // Aliases — Claude may use these names when building systems
  reservoir: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "pressure_boundary"; elevation: { value: number; unit: string; }; pressure: { value: number; unit: string; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; pressure_type?: "total" | "static" | undefined; } =>
    createPressureBoundary(id, { name: `Reservoir ${id}`, pressure: { value: 101325, unit: 'Pa' }, diagram: { position: pos } }),
  assigned_pressure: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "pressure_boundary"; elevation: { value: number; unit: string; }; pressure: { value: number; unit: string; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; pressure_type?: "total" | "static" | undefined; } =>
    createPressureBoundary(id, { name: `PB ${id}`, pressure: { value: 101325, unit: 'Pa' }, diagram: { position: pos } }),
  assigned_flow: (id, pos): { id: string; diagram: { position: { x: number; y: number; }; }; name: string; type: "flow_demand"; elevation: { value: number; unit: string; }; operation: { mode: "flow_in"; value: number; unit: string; } | { mode: "flow_out"; value: number; unit: string; }; note?: string | undefined; design_status?: "undesigned" | "sizing" | "designed" | undefined; custom_field_values?: Record<string, string | number | boolean> | undefined; layer_id?: string | undefined; calc?: { flow?: { value: number; unit: string; } | undefined; pressure?: { value: number; unit: string; } | undefined; differential_total_pressure?: { value: number; unit: string; } | undefined; suction_total_pressure?: { value: number; unit: string; } | undefined; discharge_total_pressure?: { value: number; unit: string; } | undefined; total_pressure?: { value: number; unit: string; } | undefined; } | undefined; } =>
    createFlowDemand(id, { name: `FD ${id}`, operation: { mode: 'flow_out', value: 0, unit: 'kg/s' }, diagram: { position: pos } }),
}

/**
 * Creates a vertex of the given type at the specified position using the
 * appropriate shared factory. Falls back to junction for unknown types.
 */
export function createVertexAtPosition(
  id: string,
  type: string,
  position: { x: number; y: number },
): Vertex {
  const factory = FACTORY_MAP[type]
  if (factory) return factory(id, position)
  // Fallback for truly unknown types — should not happen in practice
  return createJunction(id, { name: `${type} ${id}`, diagram: { position } })
}
